import { useState, useEffect } from "react";
import { Dropdown, IDropdownOption, Label, TooltipHost } from "@fluentui/react";
import { FiHelpCircle } from "react-icons/fi";
import { GptDeployment } from "../../api";
import styles from "./ModelPicker.module.css";

interface Props {
    className?: string;
    deployments: GptDeployment[];
    selectedGptDeployment?: string;
    onGptDeploymentChange: (deployment: string) => void;
}

const formatModelName = (name: string) => name.replace(/^gpt/i, "GPT");

const tooltipHtml = "Choose a specific GPT Model<br />Hover over each model for more details.";

const modelTooltips: { [key: string]: string } = {
    "gpt-35-turbo": "<b>GPT-3.5 Turbo</b><br />A fast and cost-effective model ideal for everyday tasks such as drafting, summarization, and basic coding.",
    "gpt-35-turbo-16k": "<b>GPT-3.5 Turbo 16K</b><br />All the capabilities of GPT-3.5 Turbo, with extended support for longer inputs and documents.",
    "gpt-4":
        "<b>GPT-4</b><br />A powerful model with strong reasoning, creativity, and coding abilities. Well-suited for more complex tasks and nuanced outputs.",
    "gpt-4-32k": "<b>GPT-4 32K</b><br />A variant of GPT-4 designed for handling longer context and multi-step reasoning across larger documents.",
    "gpt-4o":
        "<b>GPT-4o</b><br />The fastest and most efficient GPT-4 model available. Great for high-quality responses with low latency and large context support.",
    "gpt-4o-mini": "<b>GPT-4o Mini</b><br />A lightweight version of GPT-4o that balances performance and speed for simpler or high-frequency tasks.",
    "gpt-4.1":
        "<b>GPT-4.1</b><br />The latest evolution of GPT-4, with improved reasoning, reliability, and long context support. Ideal for demanding workloads.",
    "gpt-4.1-mini":
        "<b>GPT-4.1 Mini</b><br />A smaller and faster GPT-4.1 variant. Balances strong performance with lower cost - perfect for general-purpose use.",
    "gpt-4.1-nano":
        "<b>GPT-4.1 Nano</b><br />An ultra-light model designed for rapid, low-cost responses. Best for simple queries, UI helpers, or high-volume usage.",
    "gpt-4.5-preview":
        "<b>GPT-4.5 Preview</b><br />An early release of upcoming GPT-4.5 capabilities, offering enhanced accuracy and performance for advanced users.",
    "gpt-5":
        "<b>GPT-5</b><br />The next generation of GPT, delivering major advances in reasoning, adaptability, and efficiency. Supports very long contexts and excels at complex, multi-domain tasks.",
    "gpt-5-chat":
        "<b>GPT-5 Chat</b><br />A conversationally tuned GPT-5 model, optimised for interactive, multi-turn dialogue with enhanced context retention and natural tone.",
    "gpt-5-mini":
        "<b>GPT-5 Mini</b><br />A faster, lighter GPT-5 variant optimised for everyday use. Balances strong reasoning with lower cost and reduced latency.",
    "gpt-5-nano":
        "<b>GPT-5 Nano</b><br />An ultra-light GPT-5 model built for speed and efficiency. Best suited for quick responses, assistants, and high-frequency queries.",
    "gpt-5-pro":
        "<b>GPT-5 Pro</b><br />A high-end GPT-5 reasoning model built for maximum capability on complex tasks, with long-context support and robust tool use (structured outputs, functions, tools).",
    "gpt-5.1":
        "<b>GPT-5.1</b><br />An enhanced GPT-5 series model with improved accuracy, stronger reasoning, and more reliable long-context performance. Ideal for complex workflows that demand consistent, high quality output.",
    "gpt-5.1-chat":
        "<b>GPT-5.1 Chat</b><br />A conversationally optimised variant of GPT-5.1 designed for multi-turn interactions, better memory within sessions, and more natural dialogue. Perfect for assistants and customer facing chat experiences.",
    "gpt-5.2":
        "<b>GPT-5.2</b><br />Top-tier GPT-5 reasoning model with very long context support and strong tool use (functions, structured outputs, parallel tool calling). Best for complex, high-stakes workflows.",
    "gpt-5.2-chat":
        "<b>GPT-5.2 Chat</b><br />A chat-optimised GPT-5.2 variant (Preview). Built for interactive, multi-turn experiences with strong tool support and long-context chat workloads.",
    o1: "<b>o1</b><br />A first-generation reasoning model that spends more time thinking before it answers. Strong at complex problem-solving and debugging code.",
    "o1-mini": "<b>o1-Mini</b><br />A faster, cost-effective o1 variant optimised for STEM and coding tasks.",
    o3: "<b>o3</b><br />A well-rounded, state-of-the-art reasoning model across text, code, and images with full tool use. Ideal for deep, multi-step analysis.",
    "o3-mini": "<b>o3-Mini</b><br />A small, speedy o-series model that improves on o1-mini for coding and STEM with better throughput.",
    "o3-pro":
        "<b>o3-Pro</b><br />Uses more compute to think longer and provide the most reliable responses. Best for research-grade work and the toughest tasks.",
    "o4-mini":
        "<b>o4-Mini</b><br />A next-gen small reasoning model optimised for speed and cost. Excellent on maths, coding, and visual reasoning; great for high-volume use."
};

export const ModelPicker = ({ className, deployments, selectedGptDeployment, onGptDeploymentChange }: Props) => {
    const [updateTimeout, setUpdateTimeout] = useState<NodeJS.Timeout | null>(null);

    const renderTooltip = (modelName?: string) => {
        if (!modelName) return null;
        const html = modelTooltips[modelName.toLowerCase()] ?? "";
        if (!html) return null;
        return <div dangerouslySetInnerHTML={{ __html: html }} />;
    };

    const onRenderOption = (option?: IDropdownOption, defaultRender?: (properties?: IDropdownOption) => JSX.Element | null): JSX.Element | null => {
        if (!option) return null;

        const content = defaultRender ? defaultRender(option) : <span>{option.text}</span>;
        const modelName = option.data?.deployment?.modelName as string | undefined;

        return <TooltipHost tooltipProps={{ onRenderContent: () => renderTooltip(modelName) }}>{content}</TooltipHost>;
    };

    const onDeploymentChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        // Clear any existing timeout to prevent multiple updates
        if (updateTimeout) clearTimeout(updateTimeout);

        // Set a timeout to wait before calling onGptDeploymentChange
        const newTimeout = setTimeout(() => {
            if (option) {
                const deploymentName = option.key as string;
                onGptDeploymentChange(deploymentName);
            }
        }, 100); // Wait 100ms before updating

        setUpdateTimeout(newTimeout);
    };

    const dropdownOptions: IDropdownOption[] = deployments.map((d, index) => ({
        key: d.deploymentName,
        text: `${formatModelName(d.modelName)} (${d.modelVersion})`,
        data: { deployment: d },
        index: index
    }));

    useEffect(() => {
        return () => {
            if (updateTimeout) clearTimeout(updateTimeout);
        };
    }, [updateTimeout]);

    return (
        <div className={`modelPicker ${styles.container} ${className ?? ""}`}>
            <Label>
                Model Selection&nbsp;
                <FiHelpCircle data-tooltip-id="ModelPicker-tooltip" data-tooltip-html={tooltipHtml}></FiHelpCircle>
            </Label>
            <Dropdown
                className={className}
                placeholder={"Loading..."}
                options={dropdownOptions}
                selectedKey={selectedGptDeployment}
                onChange={onDeploymentChange}
                onRenderOption={onRenderOption}
            />
        </div>
    );
};
